import { useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import { getStorageCachedURL } from '@gitbook/app-database';
import { CHANGE_REQUEST_HIVE_BRANCH_PREFIX } from '@gitbook/app-hive';
import {
    type ChangedRevisionFile,
    type ChangedRevisionPage,
    getRevisionCreationTime,
    isMergeRevision,
    isRollbackRevision,
    type MergeRevision,
    type RevisionInfo,
    type RevisionPage,
    RevisionPageType,
    type RevisionSemanticChange,
    RevisionSemanticChangeType,
    RevisionType,
    type RollbackRevision,
    unformatBranchIdOrThrow,
} from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { DateShortRelative, IntlPluralize, intlFormatList } from '@gitbook/react-uikit';
import { Link } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assertNever } from '@gitbook/util-ts';

import { NavigationLink } from '../../navigation';
import { apiQuery } from '../../state';

/**
 * Short summary of changes made in a revision.
 */
export function HiveRevisionChanges(props: {
    revision: RevisionInfo;
    isDefaultBranch: boolean;
    changes: RevisionSemanticChange[];
    more?: number;
}): React.ReactElement {
    const { revision, isDefaultBranch, changes, more = 0 } = props;

    if (isRollbackRevision(revision)) {
        return <RevisionRollbackSummary revision={revision} />;
    }

    if (isMergeRevision(revision)) {
        return <RevisionMergeSummary revision={revision} changes={changes} />;
    }

    if (revision.type === RevisionType.Update) {
        return <RevisionUpdateSummary isDefaultBranch={isDefaultBranch} revision={revision} />;
    }

    if (changes.length === 0) {
        return <>created this {isDefaultBranch ? 'space' : 'change request'}</>;
    }

    if (changes.length > 0) {
        const rest = more + changes.length - 1;
        return (
            <>
                <RevisionChangesLine
                    isDefaultBranch={isDefaultBranch}
                    revision={revision}
                    change={changes[0]}
                />
                {rest > 0 ? (
                    <>
                        {' '}
                        and made {rest} other{' '}
                        <IntlPluralize count={rest} singular="change" plural="changes" />
                    </>
                ) : null}
            </>
        );
    }

    return <>made {changes.length} changes</>;
}

/**
 * Description when the revision is a rollback.
 */
function RevisionRollbackSummary(props: { revision: RollbackRevision }): React.ReactElement {
    const { revision } = props;

    const hiveClient = useHiveClient();
    const rollbackRevision = getSuspense(
        hiveClient.store.getRevision(revision.contentId, revision.parents[1], { metadata: false })
    );

    if (!rollbackRevision) {
        throw new Error('expected rollback revision to exist');
    }

    const createdAt = getRevisionCreationTime(rollbackRevision);

    return (
        <>
            rollbacked to content from <DateShortRelative value={createdAt} onlyDate={false} />
        </>
    );
}

/**
 * Description when the revision is a change-request update.
 */
function RevisionUpdateSummary(props: {
    isDefaultBranch: boolean;
    revision: RevisionInfo;
}): React.ReactElement {
    const { isDefaultBranch, revision } = props;

    const hiveClient = useHiveClient();
    const parentId = !isDefaultBranch ? revision.parents[2] : revision.parents[0];
    const primaryRevision = getSuspense(hiveClient.store.getRevision(revision.contentId, parentId));

    if (!primaryRevision) {
        throw new Error('expected primary revision to exist');
    }

    const createdAt = getRevisionCreationTime(primaryRevision);

    return (
        <>
            updated this change request with changes from primary from{' '}
            <DateShortRelative value={createdAt} onlyDate={false} />
        </>
    );
}

/**
 * Description when the revision is a merge.
 */
function RevisionMergeSummary(props: {
    revision: MergeRevision;
    changes: RevisionSemanticChange[];
}): React.ReactElement {
    const { revision, changes } = props;
    const changeRequestId = unformatBranchIdOrThrow(
        revision.mergedFrom,
        CHANGE_REQUEST_HIVE_BRANCH_PREFIX
    );
    const { data: changeRequest } = useSuspenseQuery(
        apiQuery(
            'getChangeRequestById',
            { spaceId: revision.contentId, changeRequestId },
            { nullOnError: true }
        )
    );

    if (!changeRequest) {
        return <>merged a change request</>;
    }

    const catchPlurality = changes.length > 1 ? 'changes' : 'change';

    const link = changeRequest.subject
        ? `${changeRequest.subject} (#${changeRequest?.number})`
        : `change request #${changeRequest?.number}`;

    return (
        <>
            merged {changes.length} {catchPlurality} from{' '}
            <NavigationLink
                to={{
                    screen: {
                        type: 'space',
                        space: revision.contentId,
                        changeRequest: changeRequest.id,
                        revision: undefined,
                        page: '',
                        sideSheet: undefined,
                    },
                }}
            >
                {link}
            </NavigationLink>
        </>
    );
}

/**
 * Description for a line of RevisionSemanticChange.
 */
function RevisionChangesLine(props: {
    isDefaultBranch: boolean;
    revision: RevisionInfo;
    change: RevisionSemanticChange;
}): React.ReactElement {
    const { isDefaultBranch, revision, change } = props;

    switch (change.type) {
        case RevisionSemanticChangeType.PageCreated:
            return (
                <>
                    created the page{' '}
                    <LinkToPage
                        spaceKey={revision.contentId}
                        revisionId={revision.id}
                        page={change.page}
                    />
                </>
            );
        case RevisionSemanticChangeType.PageDeleted: {
            const isNonDefaultBranchUpdateRevision =
                !isDefaultBranch && [RevisionType.Update].includes(revision.type);
            const previousRevisionId = isNonDefaultBranchUpdateRevision
                ? revision.parents[2]
                : revision.parents[0];

            return previousRevisionId ? (
                <>
                    deleted the page{' '}
                    <LinkToPage
                        spaceKey={revision.contentId}
                        revisionId={previousRevisionId}
                        page={change.page}
                    />
                </>
            ) : (
                <>deleted the page {change.page.title}</>
            );
        }
        case RevisionSemanticChangeType.PageEdited:
            return (
                <>
                    modified the page{' '}
                    <LinkToPage
                        spaceKey={revision.contentId}
                        revisionId={revision.id}
                        page={change.page}
                    />
                </>
            );
        case RevisionSemanticChangeType.PageMoved:
            return (
                <>
                    moved the page{' '}
                    <LinkToPage
                        spaceKey={revision.contentId}
                        revisionId={revision.id}
                        page={change.page}
                    />{' '}
                    in{' '}
                    {change.parent.after ? (
                        <LinkToPage
                            spaceKey={revision.contentId}
                            revisionId={revision.id}
                            page={change.parent.after}
                        />
                    ) : (
                        'root pages'
                    )}
                </>
            );
        case RevisionSemanticChangeType.FileCreated:
            return (
                <>
                    created the file <LinkToFile file={change.file} />
                </>
            );
        case RevisionSemanticChangeType.FileDeleted:
            return (
                <>
                    deleted the file <LinkToFile file={change.file} />
                </>
            );
        case RevisionSemanticChangeType.FileEdited:
            return (
                <>
                    edited the file <LinkToFile file={change.file} />
                </>
            );
        case RevisionSemanticChangeType.VariablesEdited:
            return <>modified the variables {intlFormatList(Object.keys(change.variables))}</>;
        case RevisionSemanticChangeType.ReusableContentCreated:
            return <>created the reusable content {change.reusableContent.title}</>;
        case RevisionSemanticChangeType.ReusableContentDeleted:
            return <>deleted the reusable content {change.reusableContent.title}</>;
        case RevisionSemanticChangeType.ReusableContentEdited:
            return <>edited the reusable content {change.reusableContent.title}</>;
        default:
            assertNever(change);
    }
}

function LinkToPage(props: {
    spaceKey: string;
    revisionId: string;
    page: ChangedRevisionPage<RevisionPage>;
}): React.ReactElement {
    const { spaceKey, revisionId, page } = props;

    if (page.type === RevisionPageType.Link) {
        return <>{page.title}</>;
    }

    return (
        <NavigationLink
            to={{
                screen: {
                    type: 'space',
                    space: spaceKey,
                    revision: revisionId,
                    page: page.path,
                    sideSheet: undefined,
                },
            }}
        >
            {' '}
            {page.title}
        </NavigationLink>
    );
}

function LinkToFile(props: { file: ChangedRevisionFile }): React.ReactElement {
    const { file } = props;
    return <Link href={getStorageCachedURL(file.downloadURL)}>{file.name}</Link>;
}
